import { useState, FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
// Import Types
import type { ChartData, ChartFilterModel } from './types';
// Import UI Components
import { BusyLoader, BarChart, Empty, ModuleTitle } from '@geneui/components';
import Filters from './Filters';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { DashboardHttpService } from 'services/http';
// Import Helpers
import { chartFormatter, parseChartData } from './helper';
// Import Constants
import { l } from 'constants/common';

interface Props {
    parentDate: string[];
}

const LinkOpenClickPerformanceChart: FC<Props> = ({ parentDate }) => {
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();
    const [isLoading, setIsLoading] = useState(false);
    const [chartData, setChartData] = useState<Nullable<ChartData>>(null);

    const { getChannelOpenClickDataRequest } = useCancelableRequests(
        { getChannelOpenClickDataRequest: DashboardHttpService.getChannelOpenClickData },
        { takeLatest: ['getChannelOpenClickDataRequest'] },
    );

    const fetchChartData = useCallback(
        (filters: ChartFilterModel) => {
            if (isEmpty(filters.deliveryMethods)) {
                setChartData(null);
                return;
            }

            setIsLoading(true);

            doPostRequest(getChannelOpenClickDataRequest.request, {
                requestBody: filters,
                successCallback: (data) => {
                    const parsedData = parseChartData(data, t);
                    setChartData(parsedData);
                },
                errorCallback: () => {
                    setChartData(null);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsLoading(false);
                }
            });
        },
        [doPostRequest, getChannelOpenClickDataRequest.request, t],
    );

    return (
        <>
            <div className="charts-wrap-inner-head">
                <ModuleTitle title={t(l.OpenClickPerformanceVisualization)} />
                <Filters parentDate={parentDate} onChange={fetchChartData} />
            </div>
            {isLoading ? (
                <BusyLoader spinnerSize="big" isBusy={true} />
            ) : isEmpty(chartData?.categories) ? (
                <Empty title={t(l.NoDataToDisplay)} />
            ) : (
                <div className="charts-wrap-inner-content">
                    <div
                        style={{
                            minWidth:
                                chartData && chartData.categories.length > 5
                                    ? `${chartData.categories.length * 120}px`
                                    : '100%',
                            paddingBottom: 16,
                        }}
                    >
                        <BarChart
                            series={chartData?.series ?? []}
                            categories={chartData?.categories ?? []}
                            plotOptions={{
                                column: {
                                    borderRadius: 4,
                                    columnWidth: 40,
                                    minPointLength: 3,
                                },
                            }}
                            tooltip={{
                                shared: true,
                                headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
                                formatter: function () {
                                    return chartFormatter(this, t);
                                },
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default LinkOpenClickPerformanceChart;
